'use client';

/**
 * DataTablePagination Component
 *
 * Pagination controls with glassmorphism styling and neon accents.
 */

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import type { DataTablePaginationProps } from '../../types/dataTable';

// ============================================================================
// Icon Components
// ============================================================================

const FirstPageIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z" />
  </svg>
);

const LastPageIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

interface StyledIconButtonProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
}

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledIconButtonProps>(({ theme, $neonAccent, $isDark }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    padding: theme.spacing(0.75),
    color: $isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    transition: transitionPresets.allFast,

    '&:hover:not(:disabled)': {
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
      color: neonStyles ? neonStyles.base : undefined,
    },

    '&:disabled': {
      opacity: 0.3,
    },
  };
});

interface StyledSelectContainerProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
}

const StyledSelectContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledSelectContainerProps>(({ theme, $neonAccent, $isDark }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    '& .MuiSelect-select': {
      padding: theme.spacing(0.75, 1),
      paddingRight: theme.spacing(3),
      fontSize: '0.875rem',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: $isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: neonStyles ? neonStyles.border : $isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    },

    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
    },
  };
});

const InfoText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const ControlsWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const PageSizeWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

// ============================================================================
// Component
// ============================================================================

export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  page,
  pageSize,
  totalRows,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  neonAccent,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();

  const totalPages = Math.ceil(totalRows / pageSize);
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  const canGoPrevious = page > 0;
  const canGoNext = page < totalPages - 1;

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onPageSizeChange(Number(event.target.value));
  };

  return (
    <StyledContainer $isDark={isDark}>
      <PageSizeWrapper>
        <InfoText>{t('common.rowsPerPage')}</InfoText>
        <StyledSelectContainer $neonAccent={neonAccent} $isDark={isDark}>
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            size="small"
            variant="outlined"
            MenuProps={{
              slotProps: {
                backdrop: { invisible: true },
              },
            }}
          >
            {pageSizeOptions.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </StyledSelectContainer>
      </PageSizeWrapper>

      <InfoText>{totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : t('common.noResultsCount')}</InfoText>

      <ControlsWrapper>
        <StyledIconButton
          $neonAccent={neonAccent}
          $isDark={isDark}
          onClick={() => onPageChange(0)}
          disabled={!canGoPrevious}
          size="small"
          aria-label={t('a11y.firstPage')}
        >
          <FirstPageIcon />
        </StyledIconButton>
        <StyledIconButton
          $neonAccent={neonAccent}
          $isDark={isDark}
          onClick={() => onPageChange(page - 1)}
          disabled={!canGoPrevious}
          size="small"
          aria-label={t('a11y.previousPage')}
        >
          <ChevronLeftIcon />
        </StyledIconButton>
        <StyledIconButton
          $neonAccent={neonAccent}
          $isDark={isDark}
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext}
          size="small"
          aria-label={t('a11y.nextPage')}
        >
          <ChevronRightIcon />
        </StyledIconButton>
        <StyledIconButton
          $neonAccent={neonAccent}
          $isDark={isDark}
          onClick={() => onPageChange(totalPages - 1)}
          disabled={!canGoNext}
          size="small"
          aria-label={t('a11y.lastPage')}
        >
          <LastPageIcon />
        </StyledIconButton>
      </ControlsWrapper>
    </StyledContainer>
  );
};

export default DataTablePagination;
